/**
 * Zustand store for the Condition Guide entries shown on the storefront.
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const CONDITION_GUIDE_KEY = "khays_condition_guide";

export interface DescriptionLine {
  text: string;
  bullet: boolean;
  bold: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  monospace?: boolean;
  size: "small" | "normal" | "large";
}

export interface ConditionGuideEntry {
  grade: string;
  description: (string | DescriptionLine)[];
  note?: string;
}

export function normalizeDescLine(d: string | DescriptionLine): DescriptionLine {
  if (typeof d === "string") {
    return { text: d, bullet: true, bold: false, italic: false, strikethrough: false, monospace: false, size: "normal" };
  }
  return {
    text: d.text,
    bullet: d.bullet ?? true,
    bold: d.bold ?? false,
    italic: d.italic ?? false,
    strikethrough: d.strikethrough ?? false,
    monospace: d.monospace ?? false,
    size: d.size ?? "normal",
  };
}

function getDefaultGuide(): ConditionGuideEntry[] {
  return [
    { grade: "Grade A", description: [
      { text: "Near-perfect pre-owned device", bullet: true, bold: false, size: "normal" },
      { text: "No parts changed", bullet: true, bold: false, size: "normal" },
      { text: "Battery Health 100%", bullet: true, bold: false, size: "normal" },
      { text: "Moderately priced", bullet: true, bold: false, size: "normal" },
    ] },
    { grade: "Grade B+", description: [
      { text: "Pristine condition", bullet: true, bold: false, size: "normal" },
      { text: "Possible micro-scratches", bullet: true, bold: false, size: "normal" },
      { text: "Battery Health 90%+", bullet: true, bold: false, size: "normal" },
    ] },
    { grade: "Grade B", description: [
      { text: "Used device", bullet: true, bold: false, size: "normal" },
      { text: "Some units may have replaced parts", bullet: true, bold: false, size: "normal" },
      { text: "Battery Health 95%+", bullet: true, bold: false, size: "normal" },
      { text: "Face ID functional", bullet: true, bold: false, size: "normal" },
    ], note: "⚠️ Confirm original screen status for iPhone 12+." },
  ];
}

function loadFromStorage(): ConditionGuideEntry[] {
  try {
    const stored = localStorage.getItem(CONDITION_GUIDE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return getDefaultGuide();
}

interface ConditionGuideState {
  entries: ConditionGuideEntry[];
  _rev: number;
  setEntries: (entries: ConditionGuideEntry[]) => void;
  reload: () => void;
}

export const useConditionGuideStore = create<ConditionGuideState>()(
  devtools(
    (set, get) => ({
      entries: loadFromStorage(),
      _rev: 0,
      setEntries: (entries) => {
        localStorage.setItem(CONDITION_GUIDE_KEY, JSON.stringify(entries));
        set({ entries, _rev: get()._rev + 1 });
      },
      reload: () => set({ entries: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "ConditionGuideStore" }
  )
);

export function loadConditionGuide(): ConditionGuideEntry[] {
  return useConditionGuideStore.getState().entries;
}

export function saveConditionGuide(entries: ConditionGuideEntry[]): void {
  useConditionGuideStore.getState().setEntries(entries);
}
